import {
  Array,
  Context,
  Data,
  Effect,
  HashSet,
  Layer,
  Match,
  Number,
  Option,
  Semaphore,
  Stream,
  SubscriptionRef,
} from 'effect';

import { ALERT_MESSAGES_CYCLE_INTERVAL_MS } from '@vs-shop/engine/config/alertMessages';
import { AlertMessagesDismissed } from '../alertMessagesDismissed/AlertMessagesDismissed';
import type { Announcement } from './Announcement';

export type AnnouncementGroup = 'banner' | 'base';
type Empty = Readonly<Record<never, never>>;
export type AnnouncementReadiness = Data.TaggedEnum<{
  Loading: Empty;
  Ready: Empty;
  ReadFailed: Empty;
}>;
export const AnnouncementReadiness = Data.taggedEnum<AnnouncementReadiness>();

export type AnnouncementSnapshot = {
  readonly readiness: AnnouncementReadiness;
  readonly banner: Option.Option<Announcement>;
  readonly base: Option.Option<Announcement>;
};

type Group = { readonly messages: readonly Announcement[]; readonly index: number };
type Model = {
  readonly readiness: AnnouncementReadiness;
  readonly saved: HashSet.HashSet<string>;
  readonly temporary: HashSet.HashSet<string>;
  readonly banner: Group;
  readonly base: Group;
};

const idOf = (message: Announcement) => Option.fromNullishOr(message.id).pipe(Option.map(String));
const eligible = (group: Group, model: Model) =>
  Array.filter(
    group.messages,
    (message) =>
      !Option.exists(
        idOf(message),
        (id) => HashSet.has(model.saved, id) || HashSet.has(model.temporary, id)
      )
  );
const position = (index: number, length: number) =>
  length === 0 ? 0 : Number.remainder(index, length);
const selected = (group: Group, model: Model) =>
  Match.value(model.readiness).pipe(
    Match.tag('Loading', () => Option.none<Announcement>()),
    Match.tag('Ready', 'ReadFailed', () => Array.get(eligible(group, model), group.index)),
    Match.exhaustive
  );
const snapshot = (model: Model): AnnouncementSnapshot => ({
  readiness: model.readiness,
  banner: selected(model.banner, model),
  base: selected(model.base, model),
});

const preserveSelection = (previous: Group, next: Group, before: Model, after: Model): Group => {
  const currentId = Array.get(eligible(previous, before), previous.index).pipe(
    Option.flatMap(idOf)
  );
  const messages = eligible(next, after);
  const index = Option.flatMap(currentId, (id) =>
    Array.findFirstIndex(messages, (message) => Option.contains(idOf(message), id))
  ).pipe(Option.getOrElse(() => position(previous.index, messages.length)));
  return { ...next, index };
};

const make = Effect.gen(function* () {
  const persistence = yield* AlertMessagesDismissed;
  const writes = yield* Semaphore.make(1);
  const model = yield* SubscriptionRef.make<Model>({
    readiness: AnnouncementReadiness.Loading(),
    saved: HashSet.empty(),
    temporary: HashSet.empty(),
    banner: { messages: [], index: 0 },
    base: { messages: [], index: 0 },
  });

  yield* persistence.getDismissedIds.pipe(
    Effect.matchEffect({
      onSuccess: (saved) =>
        SubscriptionRef.update(model, (state) => ({
          ...state,
          saved,
          readiness: AnnouncementReadiness.Ready(),
          banner: { ...state.banner, index: 0 },
          base: { ...state.base, index: 0 },
        })),
      onFailure: (error) =>
        Effect.logWarning('Announcement dismissal read failed.', error).pipe(
          Effect.andThen(
            SubscriptionRef.update(model, (state) => ({
              ...state,
              readiness: AnnouncementReadiness.ReadFailed(),
              banner: { ...state.banner, index: 0 },
              base: { ...state.base, index: 0 },
            }))
          )
        ),
    }),
    Effect.forkScoped
  );

  yield* Effect.sleep(ALERT_MESSAGES_CYCLE_INTERVAL_MS).pipe(
    Effect.andThen(
      SubscriptionRef.update(model, (state) => ({
        ...state,
        banner: {
          ...state.banner,
          index: position(
            Number.increment(state.banner.index),
            eligible(state.banner, state).length
          ),
        },
        base: {
          ...state.base,
          index: position(Number.increment(state.base.index), eligible(state.base, state).length),
        },
      }))
    ),
    Effect.forever,
    Effect.forkScoped
  );

  const persist = writes
    .withPermit(
      Effect.gen(function* () {
        const pending = (yield* SubscriptionRef.get(model)).temporary;
        yield* Effect.forEach(pending, (id) => persistence.addDismissedId(id), { discard: true });
        yield* SubscriptionRef.update(model, (state) => ({
          ...state,
          saved: HashSet.union(state.saved, pending),
          temporary: HashSet.difference(state.temporary, pending),
        }));
      })
    )
    .pipe(
      Effect.catch((error) => Effect.logWarning('Announcement dismissal write failed.', error))
    );

  return {
    getSnapshot: SubscriptionRef.get(model).pipe(Effect.map(snapshot)),
    changes: SubscriptionRef.changes(model).pipe(Stream.map(snapshot)),
    setMessages: (group: AnnouncementGroup, messages: readonly Announcement[]) =>
      SubscriptionRef.update(model, (state) =>
        Match.value(group).pipe(
          Match.when('banner', () => ({
            ...state,
            banner: preserveSelection(state.banner, { messages, index: 0 }, state, state),
          })),
          Match.when('base', () => ({
            ...state,
            base: preserveSelection(state.base, { messages, index: 0 }, state, state),
          })),
          Match.exhaustive
        )
      ),
    dismiss: (id: string) =>
      Effect.gen(function* () {
        yield* SubscriptionRef.update(model, (state) => {
          const next = { ...state, temporary: HashSet.add(state.temporary, id) };
          return {
            ...next,
            banner: preserveSelection(state.banner, state.banner, state, next),
            base: preserveSelection(state.base, state.base, state, next),
          };
        });
        yield* persist;
      }),
  };
});

export class Announcements extends Context.Service<Announcements, Effect.Success<typeof make>>()(
  '@vs-shop/engine/Announcements'
) {
  static readonly layer = Layer.effect(Announcements, make);
}
